import type React from "react";
import { useEffect, useRef, useState } from "react";

import { GET } from "../../../lib/api";
import { Icon } from "../../../ui";

import styles from "./ChatbotButton.module.css";

const normalizeBaseUrl = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") {
    return "";
  }

  return trimmed.replace(/\/+$/, "");
};

const runtimeMiddlewareBaseUrl = normalizeBaseUrl(
  window.MetabaseBootstrap?.["middleware-base-url"],
);
const globalMiddlewareBaseUrl = normalizeBaseUrl(
  (window as any).MB_MIDDLEWARE_BASE_URL,
);
const buildTimeMiddlewareBaseUrl = normalizeBaseUrl(
  process.env.MB_MIDDLEWARE_BASE_URL,
);

const middlewareBaseUrl =
  runtimeMiddlewareBaseUrl ||
  globalMiddlewareBaseUrl ||
  buildTimeMiddlewareBaseUrl;
const middlewareUrl = (path: string) =>
  middlewareBaseUrl ? `${middlewareBaseUrl}${path}` : path;
const sameOrigin = window.location.origin;

const isCrossOriginMiddleware =
  !!middlewareBaseUrl &&
  (() => {
    try {
      return new URL(middlewareBaseUrl).origin !== sameOrigin;
    } catch {
      return false;
    }
  })();

const buildMiddlewareCandidates = (path: string) => {
  const candidatePaths = isCrossOriginMiddleware
    ? [path, middlewareUrl(path)]
    : [middlewareUrl(path), path];

  return Array.from(new Set(candidatePaths));
};

const shouldTryNextEndpoint = (status: number) =>
  [401, 403, 404, 405].includes(status);

const getChatErrorMessage = (error: unknown) => {
  const rawMessage =
    error instanceof Error ? error.message : String(error ?? "");
  const message = rawMessage.toLowerCase();

  if (message.includes("content security policy")) {
    return "Chat is blocked by CSP policy. Please use same-origin /api/v1/middleware proxy on UAT domain.";
  }

  if (message.includes("504")) {
    return "Middleware gateway timeout. Please check reverse proxy route /api/v1/middleware/* to middleware service.";
  }

  if (message.includes("auth token")) {
    return "Cannot get auth token. Please ensure you are logged in and middleware token endpoint is reachable.";
  }

  return "Sorry, there was an issue connecting to the assistant. Please try again.";
};

interface Message {
  content: string;
  isUser: boolean;
  timestamp?: string;
  isTyping?: boolean;
  displayedContent?: string;
}

interface ChatbotButtonProps {
  className?: string;
}

export const ChatbotButton = ({ className }: ChatbotButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);

  const scrollToBottom = (smooth = true) => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  useEffect(() => {
    if (!isUserScrolling) {
      scrollToBottom(true);
    }
  }, [messages, isUserScrolling]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setMessages(prev => [
        {
          content: "Welcome! How can I assist you today?",
          isUser: false,
          timestamp: new Date().toLocaleTimeString(),
        },
        ...prev,
      ]);
    }
  }, [isOpen]);

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        messagesContainerRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5; // Chấp nhận sai số nhỏ
      setIsUserScrolling(!isAtBottom);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) {
      return;
    }

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [
      ...prev,
      {
        content: userMessage,
        isUser: true,
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);
    setIsLoading(true);

    try {
      const getToken = async () => {
        const tokenEndpoints = buildMiddlewareCandidates(
          "/api/v1/middleware/token",
        );
        let lastErrorMessage = "";

        for (const endpoint of tokenEndpoints) {
          try {
            const tokenData = (await GET(endpoint, {
              fetch: true,
              noEvent: true,
            })({})) as { token?: string };
            if (!tokenData?.token) {
              throw new Error("Missing token field in middleware response");
            }
            return tokenData.token;
          } catch (error: any) {
            const status =
              typeof error?.status === "number" ? error.status : undefined;
            const detail =
              typeof error?.data?.detail === "string"
                ? error.data.detail
                : typeof error?.data?.message === "string"
                  ? error.data.message
                  : "";

            lastErrorMessage = `Failed to get auth token${status ? ` (${status})` : ""}${detail ? `: ${detail}` : ""}`;
            if (status != null && shouldTryNextEndpoint(status)) {
              continue;
            }
            throw new Error(lastErrorMessage);
          }
        }

        if (!lastErrorMessage) {
          throw new Error("No middleware token endpoint available");
        }
        throw new Error(lastErrorMessage);
      };

      let jwtToken = token ?? (await getToken());
      setToken(jwtToken);

      const sendRequest = async (currentToken: string) => {
        const chatbotEndpoints = buildMiddlewareCandidates(
          "/api/v1/middleware/chatbot",
        );
        let response: Response | null = null;

        for (const endpoint of chatbotEndpoints) {
          response = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${currentToken}`,
            },
            body: JSON.stringify({ question: userMessage }),
          });

          if (
            response.ok ||
            response.status === 401 ||
            !shouldTryNextEndpoint(response.status)
          ) {
            break;
          }
        }

        if (!response) {
          throw new Error("No middleware chatbot endpoint available");
        }

        return response;
      };

      let response = await sendRequest(jwtToken);
      if (response.status === 401) {
        jwtToken = await getToken();
        setToken(jwtToken);
        response = await sendRequest(jwtToken);
      }

      if (!response.ok) {
        throw new Error("Failed to get response from assistant");
      }

      const data = await response.json();
      const responseText =
        data.message_response ??
        data.response ??
        data.answer ??
        data.message ??
        data.result?.message_response ??
        (typeof data === "string" ? data : JSON.stringify(data));
      const assistantMessage = {
        content: responseText,
        isUser: false,
        timestamp: new Date().toLocaleTimeString(),
        isTyping: true,
        displayedContent: "",
      };

      setMessages(prev => [...prev, assistantMessage]);

      let currentIndex = 0;
      const typeMessage = () => {
        if (currentIndex < responseText.length) {
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            lastMessage.displayedContent = responseText.substring(
              0,
              currentIndex + 1,
            );
            return newMessages;
          });
          currentIndex++;
          setTimeout(typeMessage, 50);
        } else {
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            lastMessage.isTyping = false;
            lastMessage.displayedContent = undefined;
            return newMessages;
          });
          setIsLoading(false);
          inputRef.current?.focus();
        }
      };

      typeMessage();
    } catch (error) {
      console.error("Error:", error);
      setMessages(prev => [
        ...prev,
        {
          content: getChatErrorMessage(error),
          isUser: false,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <button
        className={`${styles.chatButton} ${className}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle chat"
        aria-expanded={isOpen}
      >
        <Icon name="question" size={24} />
      </button>

      {isOpen && (
        <div className={styles.chatWindow}>
          <div className={styles.chatHeader}>
            <h2 className={styles.chatTitle}>Galaxy Assistant</h2>
            <button
              className={styles.closeButton}
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <Icon name="close" size={20} />
            </button>
          </div>

          <div
            className={styles.messagesContainer}
            ref={messagesContainerRef}
            onScroll={handleScroll}
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`${styles.message} ${
                  message.isUser ? styles.userMessage : styles.assistantMessage
                } ${message.isTyping ? styles.typing : ""}`}
              >
                <span>
                  {message.isTyping
                    ? message.displayedContent
                    : message.content}
                </span>
                {message.timestamp && (
                  <div className={styles.messageTime}>{message.timestamp}</div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className={`${styles.message} ${styles.assistantMessage}`}>
                <div className={styles.loadingSpinner} />
              </div>
            )}
          </div>

          <div className={styles.inputContainer}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                isLoading ? "Type your message..." : "Type your message..."
              }
              className={styles.input}
              autoFocus
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className={styles.sendButton}
              aria-label="Send message"
            >
              <Icon name="chevronright" size={20} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
